import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiScissors,
  FiHeart,
  FiCheckCircle,
} from "react-icons/fi";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Button from "../components/common/Button";
import Loader from "../components/common/Loader";
import formatCurrency from "../utils/formatCurrency";
import {
  AUTO_MOBILE_PARTY_SIZE_THRESHOLD,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  STUDIO_MODE_DESCRIPTION,
} from "../utils/constants";
import * as serviceService from "../services/serviceService";
import logoImage from "../assets/images/logoImage.png";

const safeCallGetServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }

  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }

  if (typeof serviceService.fetchServices === "function") {
    return serviceService.fetchServices();
  }

  throw new Error("Service list method was not found in serviceService.");
};

const FeaturedImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="hero-overlay flex h-56 items-center justify-center text-white">
        <p className="text-sm font-semibold uppercase tracking-[0.2em]">
          Featured Henna
        </p>
      </div>
    );
  }

  return (
    <div className="h-56 w-full overflow-hidden bg-white">
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        setLoading(true);
        setPageError("");

        const data = await safeCallGetServices();

        if (data?.statusCode && data.statusCode !== 200) {
          throw new Error(data.message || "Failed to load services.");
        }

        const serviceList = Array.isArray(data?.serviceList)
          ? data.serviceList
          : [];
        const activeServices = serviceList.filter(
          (service) => service?.active !== false
        );

        setServices(activeServices);
      } catch (err) {
        setPageError(err?.message || "Unable to load homepage data.");
      } finally {
        setLoading(false);
      }
    };

    loadHomeData();
  }, []);

  const featuredServices = useMemo(() => {
    return services.slice(0, 3);
  }, [services]);

  return (
    <div className="space-y-0">
      <section className="page-gradient border-b border-goldlight">
        <PageContainer className="py-16 md:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-golddeep">
                Nayra Henna
              </p>

              <h1 className="text-balance text-4xl font-semibold tracking-tight text-henna md:text-5xl lg:text-6xl">
                Henna for the moments you want to remember beautifully
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-8 text-muted-warm md:text-lg">
                Whether you are booking for yourself, your bridal moment, or a
                special celebration, Nayra Henna offers a smooth and thoughtful
                booking experience from start to finish.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/booking">
                  <Button variant="primary" size="lg">
                    Book Your Appointment
                  </Button>
                </Link>

                <Link to="/services">
                  <Button variant="secondary" size="lg">
                    Explore Services
                  </Button>
                </Link>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl bg-white p-4 shadow-soft">
                  <div className="flex items-center gap-2 text-henna">
                    <FiScissors />
                    <p className="text-sm font-semibold">Beautiful Service Options</p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-warm">
                    Discover henna services for bridal, party, traditional, and
                    custom occasions.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-4 shadow-soft">
                  <div className="flex items-center gap-2 text-henna">
                    <FiMapPin />
                    <p className="text-sm font-semibold">Studio or Mobile</p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-warm">
                    Your appointment style is arranged automatically based on
                    your party size.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-4 shadow-soft">
                  <div className="flex items-center gap-2 text-henna">
                    <FiCalendar />
                    <p className="text-sm font-semibold">Easy Booking Flow</p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-warm">
                    Choose your service, select your date and time, and confirm
                    your details with confidence.
                  </p>
                </div>
              </div>
            </div>

            <div className="content-card-lg overflow-hidden">
              <div className="hero-overlay flex min-h-[420px] items-center justify-center p-10 text-white">
                <div className="max-w-md text-center">
                  <p className="text-sm font-semibold uppercase tracking-[0.25em]">
                    Art • Elegance • Culture
                  </p>
                  <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
                    Henna designed with care for your special moments
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-white/90">
                    From intimate appointments to larger celebrations, your
                    experience is designed to feel graceful, welcoming, and
                    beautifully personal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </PageContainer>
      </section>

      <section className="page-section">
        <PageContainer>
          <SectionTitle
            eyebrow="About the Artist"
            title="Meet Maryan"
            subtitle="The artist behind the beauty, detail, and care of Nayra Henna."
            className="mb-8"
          />

          <div className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="content-card-lg overflow-hidden">
              <div className="flex h-[420px] w-full items-center justify-center bg-soft-cream p-6 lg:h-full lg:min-h-[500px]">
                <img
                  src={logoImage}
                  alt="Nayra Henna logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>

            <div className="content-card-lg relative overflow-hidden p-6 md:p-8 lg:min-h-[500px]">
              <div className="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-full bg-[rgba(168,127,73,0.08)] blur-3xl" />
              <div className="pointer-events-none absolute bottom-0 left-0 h-40 w-40 rounded-full bg-[rgba(139,58,58,0.07)] blur-3xl" />

              <div className="relative flex h-full flex-col">
                <div className="inline-flex w-fit items-center gap-2 rounded-full border border-goldlight bg-soft-cream px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-golddeep">
                  <FiHeart className="text-henna" />
                  Artist Story
                </div>

                <h3 className="mt-4 text-3xl font-semibold text-henna md:text-4xl">
                  Henna artistry with warmth, elegance, and intention
                </h3>

                <div className="mt-5 space-y-4 text-sm leading-7 text-muted-warm md:text-base">
                  <p>
                    Maryan brings a thoughtful and refined approach to every
                    design, blending tradition, beauty, and personal expression
                    in each appointment.
                  </p>

                  <p>
                    When you book with Nayra Henna, you are not just choosing a
                    service. You are choosing an experience shaped with patience,
                    attention to detail, and genuine care for how you want to
                    feel.
                  </p>

                  <p>
                    Whether your appointment is for a wedding, a celebration, or
                    a personal moment of adornment, her goal is to leave you
                    feeling seen, comfortable, and beautifully cared for.
                  </p>
                </div>

                <div className="mt-6 rounded-2xl border border-goldlight bg-soft-cream p-4">
                  <p className="text-sm italic leading-7 text-[#5f4b46]">
                    “Every design is created to feel meaningful, graceful, and
                    uniquely yours as you let your henna do the talking.”
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/booking">
                    <Button variant="primary">Book Your Appointment</Button>
                  </Link>

                  <Link to="/gallery">
                    <Button variant="secondary">View the Gallery</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </PageContainer>
      </section>

      <div className="flex justify-center py-10">
        <div className="flex items-center gap-4 text-henna opacity-80">
          <span className="h-px w-20 bg-goldlight"></span>
          <span className="text-xl">❁</span>
          <span className="h-px w-20 bg-goldlight"></span>
        </div>
      </div>

      <section className="page-section">
        <PageContainer>
          <SectionTitle
            eyebrow="Appointment Options"
            title="Your appointment style is chosen for you automatically"
            subtitle="The booking flow now matches the latest rules, so your service mode is set based on your total party size."
            className="mb-8"
          />

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="content-card-lg p-6 md:p-8">
              <div className="flex items-center gap-3 text-henna">
                <FiMapPin size={20} />
                <h3 className="text-2xl font-semibold">Studio Appointment</h3>
              </div>

              <p className="mt-4 text-sm leading-7 text-muted-warm">
                {STUDIO_MODE_DESCRIPTION}
              </p>

              <p className="mt-3 text-sm leading-7 text-muted-warm">
                If your total party size is less than{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {AUTO_MOBILE_PARTY_SIZE_THRESHOLD}
                </span>
                , your appointment will automatically be set to{" "}
                <span className="font-semibold text-[#3b2f2f]">Studio</span>.
              </p>

              <div className="mt-5 rounded-2xl border border-[#d7c2a3] bg-soft-cream p-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="mt-1 text-henna" />
                  <p className="text-sm leading-7 text-muted-warm">
                    A studio appointment is ideal for smaller bookings and a
                    simple, comfortable visit to the artist.
                  </p>
                </div>
              </div>
            </div>

            <div className="content-card-lg p-6 md:p-8">
              <div className="flex items-center gap-3 text-henna">
                <FiMapPin size={20} />
                <h3 className="text-2xl font-semibold">Mobile Appointment</h3>
              </div>

              <p className="mt-4 text-sm leading-7 text-muted-warm">
                {MOBILE_MODE_DESCRIPTION}
              </p>

              <p className="mt-3 text-sm leading-7 text-muted-warm">
                If your total party size is{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {AUTO_MOBILE_PARTY_SIZE_THRESHOLD}
                </span>{" "}
                or more, your appointment will automatically be set to{" "}
                <span className="font-semibold text-[#3b2f2f]">Mobile</span>,
                and you will be asked to provide the service location.
              </p>

              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="mt-1 text-henna" />
                  <p className="text-sm leading-7 text-muted-warm">
                    {MOBILE_TRAVEL_EXPENSES_DISCLAIMER}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </PageContainer>
      </section>

      <section className="page-section border-y border-goldlight bg-soft-cream">
        <PageContainer>
          <SectionTitle
            eyebrow="Featured Services"
            title="Popular Services"
            subtitle="A few beautiful options to help you get started."
            className="mb-8"
          />

          {pageError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{pageError}</p>
            </div>
          ) : loading ? (
            <Loader text="Loading featured services..." />
          ) : featuredServices.length === 0 ? (
            <div className="content-card-lg p-8">
              <div className="empty-state">
                <p className="text-base font-medium text-[#3b2f2f]">
                  Services will appear here soon.
                </p>
              </div>
            </div>
          ) : (
            <div className="responsive-three-col">
              {featuredServices.map((service) => (
                <article
                  key={service.id}
                  className="content-card overflow-hidden transition-transform duration-200 hover:-translate-y-1"
                >
                  <FeaturedImage
                    src={service?.serviceImageUrl}
                    alt={service?.serviceName || "Henna service"}
                  />

                  <div className="p-5">
                    <div className="flex flex-wrap gap-2">
                      {service?.serviceType ? (
                        <span className="badge-soft">{service.serviceType}</span>
                      ) : null}
                      {service?.hennaType ? (
                        <span className="badge-soft">{service.hennaType}</span>
                      ) : null}
                    </div>

                    <h3 className="mt-4 text-xl font-semibold text-henna">
                      {service?.serviceName || "Service"}
                    </h3>

                    <p className="mt-3 text-sm leading-6 text-muted-warm">
                      {service?.serviceDescription
                        ? service.serviceDescription.length > 100
                          ? `${service.serviceDescription.slice(0, 100)}...`
                          : service.serviceDescription
                        : "Service details coming soon."}
                    </p>

                    <div className="mt-5 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Starting Price
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.servicePrice !== undefined &&
                          service?.servicePrice !== null
                            ? formatCurrency(service.servicePrice)
                            : "Contact"}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                          Duration
                        </p>
                        <p className="mt-1 text-lg font-semibold text-henna">
                          {service?.serviceDurationMinutes
                            ? `${service.serviceDurationMinutes} min`
                            : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <Button
                        variant="secondary"
                        onClick={() =>
                          navigate(`/services?serviceId=${service.id}`)
                        }
                      >
                        View Details
                      </Button>

                      <Button
                        variant="primary"
                        onClick={() =>
                          navigate(`/booking?serviceId=${service.id}`)
                        }
                      >
                        Book This Service
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="mt-8">
            <Link to="/services">
              <Button variant="secondary" size="lg">
                View All Services
              </Button>
            </Link>
          </div>
        </PageContainer>
      </section>

      <section className="page-section">
        <PageContainer>
          <SectionTitle
            eyebrow="How It Works"
            title="Booking made simple"
            subtitle="Your booking flow is clear, guided, and aligned with the latest appointment rules."
            className="mb-8"
          />

          <div className="responsive-three-col">
            <div className="content-card-lg p-6">
              <div className="flex items-center gap-3 text-henna">
                <FiScissors size={20} />
                <h3 className="text-xl font-semibold">
                  1. Choose your service
                </h3>
              </div>
              <p className="mt-4 text-sm leading-7 text-muted-warm">
                Select the service that fits your occasion, then choose the
                henna type you would like for your appointment.
              </p>
            </div>

            <div className="content-card-lg p-6">
              <div className="flex items-center gap-3 text-henna">
                <FiClock size={20} />
                <h3 className="text-xl font-semibold">
                  2. Select your date and party size
                </h3>
              </div>
              <p className="mt-4 text-sm leading-7 text-muted-warm">
                Choose an available date and time, then enter your party details
                so your appointment can be prepared correctly.
              </p>
            </div>

            <div className="content-card-lg p-6">
              <div className="flex items-center gap-3 text-henna">
                <FiCalendar size={20} />
                <h3 className="text-xl font-semibold">
                  3. Review and confirm
                </h3>
              </div>
              <p className="mt-4 text-sm leading-7 text-muted-warm">
                Your appointment mode is set automatically, your details are
                reviewed clearly, and you can submit your booking with
                confidence.
              </p>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
};

export default Home;